import { useState, useEffect, useCallback } from "react";
import api from "../utils/api";

export default function useNotifications(userId) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // 1. Bildirimleri Getir
  const fetchNotifications = useCallback(
    async (unreadOnly = false) => {
      if (!userId) return;
      try {
        setIsLoading(true);
        setError(null);

        const endpoint = unreadOnly
          ? `notification/user/${userId}/unread`
          : `notification/user/${userId}`;

        const response = await api.get(endpoint);

        if (response.data.success) {
          setNotifications(response.data.notifications || []);
          setUnreadCount(response.data.unreadCount || 0);
        }
      } catch (err) {
        setError(err.response?.data?.message || "Bildirimler alınamadı");
      } finally {
        setIsLoading(false);
      }
    },
    [userId],
  );

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // 2. Okundu Olarak İşaretle
  const markAsRead = async (notificationId) => {
    try {
      const response = await api.put(`notification/${notificationId}/read`, {
        notificationId,
      });
      if (response.data.success) {
        setNotifications((prev) =>
          prev.map((n) =>
            n._id === notificationId ? { ...n, read: true } : n,
          ),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (err) {
      setError("İşlem başarısız.");
    }
  };

  // 3. Tümünü Okundu İşaretle
  const markAllAsRead = async () => {
    try {
      const response = await api.put(
        `notification/user/${userId}/mark-all-read`,
      );
      if (response.data.success) {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        setUnreadCount(0);
      }
    } catch (err) {
      setError("Tümünü okundu işaretleme başarısız.");
    }
  };

  // 4. Tek Bildirim Sil
  const deleteNotification = async (notificationId) => {
    try {
      const response = await api.delete(`notification/${notificationId}`);
      if (response.data.success) {
        setNotifications((prev) =>
          prev.filter((n) => n._id !== notificationId),
        );
        // Eğer silinen okunmamışsa sayıyı düşür
        const target = notifications.find((n) => n._id === notificationId);
        if (target && !target.read)
          setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (err) {
      setError("Silme işlemi başarısız.");
    }
  };

  // 5. Okunmuşları Temizle
  const clearReadNotifications = async () => {
    try {
      const response = await api.delete(`notification/user/${userId}/read`);
      if (response.data.success) {
        setNotifications((prev) => prev.filter((n) => !n.read));
      }
    } catch (err) {
      setError("Okunmuşlar temizlenemedi.");
    }
  };

  // 6. Tümünü Sil
  const clearAllNotifications = async () => {
    try {
      const response = await api.delete(`notification/user/${userId}/all`);
      if (response.data.success) {
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (err) {
      setError("Tüm bildirimler silinemedi.");
    }
  };

  // 7. Akıllı Temizleme
  const smartCleanup = async (olderThanDays = 30) => {
    try {
      const response = await api.delete(`notification/user/${userId}/cleanup`, {
        params: { deleteRead: true, olderThanDays, keepUnread: true },
      });
      if (response.data.success) await fetchNotifications();
    } catch (err) {
      setError("Temizleme hatası.");
    }
  };

  // 8. Sınıf Davetine Yanıt Ver
  const respondToInvitation = async (classId, action, notificationId) => {
    // Eğer hook'a dışarıdan gelen userId boşsa, localStorage'dan yedek al
    const currentUserId =
      userId || JSON.parse(localStorage.getItem("postUser"))?._id;

    if (!currentUserId) {
      setError("Kullanıcı bilgisi bulunamadı.");
      return false;
    }

    try {
      const response = await api.post("respondToClassInvitation", {
        classId,
        action,
        studentId: currentUserId, // Çakışma olmaması için yeni ismi kullandık
      });

      if (response.data.success) {
        await markAsRead(notificationId);
        if (action === "reject") {
          setNotifications((prev) =>
            prev.filter((n) => n._id !== notificationId),
          );
        }
        return true;
      }
    } catch (err) {
      setError("Davet yanıtlanamadı.");
      return false;
    }
  };

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearReadNotifications,
    clearAllNotifications,
    smartCleanup,
    fetchNotifications,
    respondToInvitation,
    refresh: fetchNotifications,
  };
}
